<template>
    <v-container fluid>
        <v-breadcrumbs :items="breadcrumbsItems" class="pa-0 mb-2" large></v-breadcrumbs>
        <v-card outlined>
            <v-card-title>
                <!-- <v-icon color="red darken-2" class="mr-2">mdi-shield-alert</v-icon> -->
                {{ $t('safety_alert.title') }}
                <v-chip v-if="unhandledCount > 0" small color="red darken-2" dark class="ml-3">
                    {{ $t('safety_alert.unhandled_chip').replace('${num}', unhandledCount) }}
                </v-chip>
                <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text class="d-flex flex-wrap align-center" style="gap: 16px;">
                <v-select v-if="!homeworkMode" v-model="filterHomeworkId" :items="homeworkOptions" item-text="name"
                    item-value="id" :label="$t('safety_alert.filter.homework')" dense outlined clearable hide-details
                    style="max-width: 280px;" @change="reload"></v-select>
                <v-select v-model="filterStatus" :items="statusOptions" item-text="text" item-value="value"
                    :label="$t('safety_alert.filter.status')" dense outlined clearable hide-details
                    style="max-width: 200px;" @change="reload"></v-select>
                <div class="d-flex align-center" style="gap: 8px;">
                    <DatePicker :homeworkCreateDate="homeworkCreateDate" :lastPostDate="lastPostDate"
                        :btnHeight="40" @queryData="onDateRangeChange"></DatePicker>
                    <v-chip v-if="filterStartDate" small close @click:close="clearDateRange">
                        {{ filterStartDate }} ~ {{ filterEndDate }}
                    </v-chip>
                </div>
            </v-card-text>
            <div class="mx-4">
                <v-divider></v-divider>
            </div>

            <v-data-table :headers="headers" :items="posts" :server-items-length="total" :loading="loading"
                :page.sync="page" :items-per-page="pageSize" hide-default-footer disable-sort
                :loading-text="$t('general.table.loading')" :no-data-text="$t('safety_alert.no_data')"
                class="mx-4">

                <template v-slot:item.student="{ item }">
                    <span>{{ item.student_name }}</span>
                    <br />
                    <span class="font-weight-thin text-caption">{{ item.homework_name }}</span>
                </template>

                <template v-slot:item.post_title="{ item }">
                    <a class="text-decoration-underline" @click="openPost(item)">
                        {{ item.post_title || $t('safety_alert.untitled') }}
                    </a>
                    <div class="text-caption grey--text" style="white-space: nowrap;">
                        {{ $t('safety_alert.table.date') }} {{ item.diary_date }}
                    </div>
                </template>

                <template v-slot:item.content="{ item }">
                    <div v-for="msg in item.messages" :key="msg.safety_alert_id" class="text-caption text-truncate"
                        style="max-width: 260px;" :title="msg.content">
                        {{ msg.content }}
                    </div>
                </template>

                <template v-slot:item.categories="{ item }">
                    <v-chip v-for="c in item.categories" :key="c.type" x-small class="mr-1 mb-1"
                        :color="severityColor(c.severity)" dark>
                        {{ $t(`safety_alert.category.${c.type}`) }} {{ c.severity }}
                    </v-chip>
                </template>

                <template v-slot:item.alert_time="{ item }">
                    <span class="text-caption" style="white-space: nowrap;">{{ latestAlertTime(item) }}</span>
                </template>

                <!-- 狀態用純文字+色點/icon,膠囊形狀只留給按鈕,避免 chip 被誤認為可點 -->
                <template v-slot:item.actions="{ item }">
                    <div v-if="item.status === 0" class="text-center py-2">
                        <span class="red--text text--darken-2 font-weight-medium d-inline-flex align-center"
                            style="white-space: nowrap;">
                            <v-icon x-small class="mr-1" color="red darken-2">mdi-circle</v-icon>
                            {{ $t('safety_alert.status.pending') }}
                        </span>
                        <div class="d-flex justify-center align-center mt-2" style="gap: 8px; white-space: nowrap;">
                            <v-btn small outlined class="normal-case" color="red darken-2"
                                :loading="updatingPostId === item.diary_post_id"
                                @click="openConfirm(item, 1)">
                                {{ $t('safety_alert.action.report') }}
                            </v-btn>
                            <v-btn small outlined class="normal-case"
                                :loading="updatingPostId === item.diary_post_id"
                                @click="openConfirm(item, 2)">
                                {{ $t('safety_alert.action.ignore') }}
                            </v-btn>
                        </div>
                    </div>
                    <div v-else class="text-center py-2">
                        <span class="font-weight-medium d-inline-flex align-center" style="white-space: nowrap;"
                            :class="item.status === 1 ? 'green--text text--darken-2' : 'grey--text text--darken-1'">
                            <v-icon small class="mr-1" :color="item.status === 1 ? 'green darken-2' : 'grey darken-1'">
                                mdi-check-circle-outline
                            </v-icon>
                            {{ item.status === 1 ? $t('safety_alert.status.reported') : $t('safety_alert.status.ignored') }}
                        </span>
                        <div class="text-caption grey--text mt-1" style="white-space: nowrap;">
                            {{ handledInfo(item) }}
                        </div>
                    </div>
                </template>
            </v-data-table>
            <div class="text-center my-4">
                <v-pagination v-model="page" :length="totalPage" :total-visible="11" circle color="orange accent-2">
                </v-pagination>
            </div>
        </v-card>

        <PostDialog :postId="dialogPostId" :homeworkId="dialogHomeworkId" :postDialogOpenCount="postDialogOpenCount"
            :safetyAlert="dialogAlertItem" @alert-updated="onDialogAlertUpdated"
            @alert-update-failed="onDialogAlertUpdateFailed" v-if="dialogPostId" />
        <!-- 標記確認框:記錄處理人且不可還原,操作前確認 -->
        <v-dialog v-model="confirmDialog" width="420">
            <v-card class="pb-2">
                <v-card-title class="subtitle-1">
                    {{ confirmTitle }}
                </v-card-title>
                <v-card-text>
                    {{ $t('safety_alert.confirm.description') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small class="normal-case" @click="confirmDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="red darken-2" dark small class="normal-case" @click="confirmUpdate">
                        {{ $t('button.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <SnackMsg ref="snack" />
    </v-container>
</template>

<script>
import { safetyAlertServices } from '@/services/safetyAlert.js';
import { diaryServices } from '@/services/diary.js';
import PostDialog from '@/components/console/diary/component/PostDialog.vue';
import SnackMsg from '@/components/console/diary/component/SnackMsg.vue';
import DatePicker from '@/components/console/diary/component/DatePicker.vue';
import dayjs from 'dayjs';
import { mapState } from 'vuex';

export default {
    name: 'safetyAlerts',
    components: { PostDialog, SnackMsg, DatePicker },
    data() {
        return {
            breadcrumbsItems: [],
            headers: [
                { text: this.$t('safety_alert.table.student'), value: 'student', align: 'center', class: 'grey lighten-4' },
                { text: this.$t('safety_alert.table.post_title'), value: 'post_title', align: 'center', class: 'grey lighten-4' },
                { text: this.$t('safety_alert.table.content'), value: 'content', align: 'left', class: 'grey lighten-4' },
                { text: this.$t('safety_alert.table.categories'), value: 'categories', align: 'center', class: 'grey lighten-4' },
                { text: this.$t('safety_alert.table.alert_time'), value: 'alert_time', align: 'center', class: 'grey lighten-4', width: '135px' },
                { text: this.$t('safety_alert.table.status'), value: 'actions', align: 'center', class: 'grey lighten-4', width: '190px' },
            ],
            posts: [],
            page: 1,
            pageSize: 20,
            total: 0,
            totalPage: 1,
            unhandledCount: 0,
            loading: false,
            filterHomeworkId: null,
            // 預設顯示全部;API 排序為待處理優先 → 嚴重度高 → 新,待處理自然在最上面
            filterStatus: null,
            // 以日記日期篩選(yyyy-MM-dd,與表格顯示一致)
            filterStartDate: null,
            filterEndDate: null,
            // homework 模式下提供 DatePicker 快捷按鈕(日記本建立日/最新日記日期)
            homeworkCreateDate: null,
            lastPostDate: null,
            homeworkOptions: [],
            statusOptions: [
                { text: this.$t('safety_alert.status.all'), value: null },
                { text: this.$t('safety_alert.status.pending'), value: 0 },
                { text: this.$t('safety_alert.status.handled'), value: 'handled' },
                { text: this.$t('safety_alert.status.reported'), value: 1 },
                { text: this.$t('safety_alert.status.ignored'), value: 2 },
            ],
            dialogPostId: null,
            dialogHomeworkId: null,
            dialogAlertItem: null,
            postDialogOpenCount: 0,
            updatingPostId: null,
            confirmDialog: false,
            confirmTargetItem: null,
            confirmTargetStatus: null,
        };
    },
    computed: {
        ...mapState(['homeworkName']),
        confirmTitle() {
            if (this.confirmTargetStatus == null) return '';
            const statusText = this.confirmTargetStatus === 1
                ? this.$t('safety_alert.status.reported')
                : this.$t('safety_alert.status.ignored');
            return this.$t('safety_alert.confirm.title').replace('${status}', statusText);
        },
        /** 由日記本側欄進入(/diary/homework/:id/safety):鎖定該日記本、隱藏日記本篩選 */
        homeworkMode() {
            return this.$route.params.id != null;
        },
    },
    async created() {
        if (this.homeworkMode) {
            this.filterHomeworkId = parseInt(this.$route.params.id);
            this.loadHomeworkDates();
            this.breadcrumbsItems = [
                {
                    text: this.$t('index.title'),
                    disabled: false,
                    href: '/diary',
                },
                {
                    text: this.homeworkName,
                    disabled: false,
                    href: `/diary/homework/${this.$route.params.id}`,
                },
                {
                    text: this.$t('safety_alert.title'),
                    disabled: true,
                },
            ];
        } else {
            const qsHomeworkId = parseInt(this.$route.query.homework_id);
            if (!isNaN(qsHomeworkId)) {
                this.filterHomeworkId = qsHomeworkId;
            }
            this.breadcrumbsItems = [
                {
                    text: this.$t('index.title'),
                    disabled: false,
                    href: '/diary',
                },
                {
                    text: this.$t('safety_alert.title'),
                    disabled: true,
                },
            ];
            this.loadHomeworkOptions();
        }
        this.getAlertData();
    },
    watch: {
        page() {
            this.getAlertData();
        },
        homeworkName() {
            if (this.homeworkMode && this.breadcrumbsItems.length === 3) {
                this.breadcrumbsItems[1].text = this.homeworkName;
            }
        },
    },
    methods: {
        reload() {
            this.page = 1;
            this.getAlertData();
        },
        onDateRangeChange(timeRange) {
            this.filterStartDate = timeRange?.[0] ?? null;
            this.filterEndDate = timeRange?.[1] ?? null;
            this.reload();
        },
        clearDateRange() {
            this.filterStartDate = null;
            this.filterEndDate = null;
            this.reload();
        },
        /** homework 模式:取日記本建立日與最新日記日期,供 DatePicker 快捷按鈕(同 News 頁) */
        async loadHomeworkDates() {
            try {
                const res = await diaryServices.getHomeworkName(this.$route.params.id, {});
                this.homeworkCreateDate = res.data.create_date;
                this.lastPostDate = res.data.last_post_date;
                if (this.lastPostDate != null) {
                    this.lastPostDate = new Date(this.lastPostDate)
                        .toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })
                        .replace(/\//g, '-');
                }
            } catch (err) {
                // 取不到就不顯示快捷按鈕,不影響篩選
            }
        },
        async loadHomeworkOptions() {
            try {
                const res = await diaryServices.getMyDiaryHomeworkList();
                this.homeworkOptions = res.data || [];
            } catch (err) {
                this.homeworkOptions = [];
            }
        },
        async getAlertData() {
            this.loading = true;
            try {
                const query = {
                    page: this.page,
                    page_size: this.pageSize,
                };
                if (this.filterHomeworkId != null) {
                    query.homework_id = this.filterHomeworkId;
                }
                if (this.filterStatus != null) {
                    query.status = this.filterStatus;
                }
                if (this.filterStartDate) {
                    query.start_date = this.filterStartDate;
                }
                if (this.filterEndDate) {
                    query.end_date = this.filterEndDate;
                }
                const res = await safetyAlertServices.getAlerts(query);
                this.posts = res.data.posts;
                this.total = res.data.total;
                this.totalPage = res.data.total_page || 1;
                this.unhandledCount = res.data.unhandled_count;
            } catch (err) {
                this.$refs.snack?.showAlertMsg('error', this.$t('safety_alert.msg.load_failed'));
            }
            this.loading = false;
        },
        openConfirm(item, status) {
            this.confirmTargetItem = item;
            this.confirmTargetStatus = status;
            this.confirmDialog = true;
        },
        async confirmUpdate() {
            this.confirmDialog = false;
            if (this.confirmTargetItem == null) return;
            await this.updateStatus(this.confirmTargetItem, this.confirmTargetStatus);
            this.confirmTargetItem = null;
            this.confirmTargetStatus = null;
        },
        async updateStatus(item, status) {
            this.updatingPostId = item.diary_post_id;
            try {
                await safetyAlertServices.updateByPost(item.diary_post_id, status);
                this.$refs.snack?.showAlertMsg('success', this.$t('safety_alert.msg.updated'));
                // 警示條 / badge 即時更新
                this.$root.$emit('safety-summary-refresh');
                await this.getAlertData();
            } catch (err) {
                this.$refs.snack?.showAlertMsg('error', this.$t('safety_alert.msg.update_failed'));
            }
            this.updatingPostId = null;
        },
        openPost(item) {
            this.dialogPostId = item.diary_post_id;
            this.dialogHomeworkId = item.homework_id;
            this.dialogAlertItem = item;
            this.postDialogOpenCount += 1;
        },
        // 在 PostDialog 內處理完警示 → 更新列表(summary 由 dialog 廣播)
        onDialogAlertUpdated() {
            this.$refs.snack?.showAlertMsg('success', this.$t('safety_alert.msg.updated'));
            this.getAlertData();
        },
        onDialogAlertUpdateFailed() {
            this.$refs.snack?.showAlertMsg('error', this.$t('safety_alert.msg.update_failed'));
        },
        /** 該篇最新一次警示觸發時間(= 排序依據),補顯示避免與日記日期混淆 */
        latestAlertTime(item) {
            const latest = (item.messages || []).reduce(
                (acc, m) => (!acc || (m.created_at && m.created_at > acc)) ? m.created_at : acc, null);
            return latest ? dayjs(latest).format('YYYY-MM-DD HH:mm') : '';
        },
        /** 標籤式句型(i18n 模板整句翻譯,避免詞序問題):處理人：PJ（2026-07-09 20:51） */
        handledInfo(item) {
            if (!item.handled_by_name && !item.handled_at) return '';
            return this.$t('safety_alert.handled_info')
                .replace('${name}', item.handled_by_name || '-')
                .replace('${time}', this.formatDateTime(item.handled_at));
        },
        severityColor(severity) {
            if (severity >= 3) return 'red darken-2';
            if (severity === 2) return 'orange darken-2';
            return 'amber darken-1';
        },
        formatDateTime(dt) {
            if (!dt) return '';
            return dayjs(dt).format('YYYY-MM-DD HH:mm');
        },
    },
};
</script>

<style scoped>
/* Vuetify 預設 td 上下 padding 為 0(靠 48px 最小列高撐),
   兩行內容會貼分隔線,補 8px 上下間距 */
.v-data-table ::v-deep tbody td {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    vertical-align: middle;
}
</style>
